import React, { useContext, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';

import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { Link } from 'app/src/utils/routing';

import { ACCOUNTS, ACCOUNTS_WITH_COUNT, HIDE_ACCOUNT } from './queries';
import SubNav from './SubNav';
import EditAccount from './edit';
import SPECIAL_USES from './specialUses';

const checkedColumn = ({ value }) => value ? '✓' : '';

const baseColumns = [
  {
    key: 'name',
    label: 'Name',
    sort: 'asc',
  },
  {
    key: 'accountGroup',
    label: 'Account Group',
    value: ({ value }) => value.name,
  },
  {
    key: 'parent',
    label: 'Parent',
    value: ({ value }) => value?.fullName,
  },
  {
    key: 'special',
    label: 'Special Use',
    value: ({ value }) => (
      SPECIAL_USES.find(special => value === special.value)?.label
    ),
  },
  {
    key: 'nextCheckNumber',
    label: 'Next Check Number',
  },
];

const Accounts = () => {
  const [showEditModal, setShowEditModal] = useState();
  const { currentUser } = useContext(AuthContext);

  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const { data } = useQuery(ACCOUNTS_WITH_COUNT,
    { variables: queryVariables }
  );

  const [hideAccount] = useMutation(HIDE_ACCOUNT, { onCompleted: refetch });

  if (!data) { return <DataState.Loading /> };

  const onHide = account => {
    if (window.confirm(`Hide ${account.name}?`)) {
      hideAccount({ variables: { id: account.id }});
    }
  };

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer>
          <Pressable onPress={() => setShowEditModal(entry)}>
            <Text>Edit</Text>
          </Pressable>
          <LinkSpace />
          {currentUser?.accounting && (
            <>
              <Link to={`/accounting/register/account/${entry.id}`}>
                <Text>Register</Text>
              </Link>
              <LinkSpace />
              <Pressable onPress={() => onHide(entry)} >
                <Text>Hide</Text>
              </Pressable>
            </>
          )}
        </LinkContainer>
      )
    },
  ];

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px'}}>
          <Text>Account Group</Text>
          <select
            type={'select'}
            name="account_group"
            onChange={e => {
              setFilter('account_group', e.target.value)}
            }
            value={filters.account_group || ''}
          >
            <option key={`account-group-any`}>{'Any'}</option>
            {
              data.accountsWithCount.accountGroups.map(group => (
              <option key={`account-group-${group.id}`} value={group.id}>
                {group.label}
              </option>
            ))}
          </select>
          <Text>Special</Text>
          <select
            type={'select'}
            name="special"
            onChange={e => {
              setFilter('special', e.target.value)}
            }
            value={filters.special || ''}
          >
            <option key={`account-special-any`}>{'Any'}</option>
            {
              data.accountsWithCount.specialTypes.map(type => (
              <option key={`account-group-${type}`} value={type}>
                {type}
              </option>
            ))}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      <SubNav refetch={refetch} selects={data} />

      <ContentContainer>
        <PageTitle>Accounts</PageTitle>

        <DataTable.Gql
          setRefetch={setRefetch}
          query={ACCOUNTS_WITH_COUNT}
          queryName='accountsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.accountsWithCount)}
          htmlTable
        />
      </ContentContainer>

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Account"
        >
          <EditAccount
            account={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
            selects={data}
          />
        </Modal>
      }
    </MainContainer>
  );
};

export default Accounts;
